import { ApiResponse } from '../types';

/**
 * Helper to extract data from ApiResponse<T>
 * The backend now wraps all responses in ApiResponse<T>
 * This helper extracts the actual data from the wrapper
 */
export function extractApiData<T>(response: ApiResponse<T>): T {
  if (response.success && response.data) {
    return response.data;
  }
  // If no success or no data, return the data anyway (might be null/undefined)
  return response.data as T;
}

/**
 * Helper to check if ApiResponse indicates success
 */
export function isApiSuccess<T>(response: ApiResponse<T>): boolean {
  return response.success === true;
}

/**
 * Helper to get error message from ApiResponse
 */
export function getApiError<T>(response: ApiResponse<T>): string {
  return response.error || 'Unknown error';
}

/**
 * Wrapper around fetch that handles ApiResponse automatically
 */
export async function fetchApi<T>(
  url: string,
  options?: RequestInit
): Promise<ApiResponse<T>> {
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `API call failed: ${response.status} ${response.statusText}. Error: ${errorText}`
      );
    }

    const apiResponse: ApiResponse<T> = await response.json();
    return apiResponse;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      data: undefined as any
    };
  }
}
